// ConnectedServicesCard — the "what's wired" panel on /settings.
//
// W5-late polish (migration 0032): each tile that maps to a runtime
// kill-switch key now gets a <Switch> in the top-right corner. The
// switch reads from app_settings via React Query (10s refetch) and
// writes optimistically with an 8-second undo toast. Tiles that don't
// map to a single owner-controllable kill switch render a small
// "Managed" badge instead (Fathom is an always-deployed webhook
// receiver, Gusto is manual upload).
//
// Status tiers (heartbeat-derived, unchanged from W4):
//   alive   → "Connected · synced X ago"  [green]
//   warning → "Stale (≥1.5× interval)"    [amber]
//   alert   → "Stopped (≥3× interval)"    [red]
//   null    → "Not reporting"             [muted]
//
// Source of truth on the toggle keys lives in migration 0032 +
// apps/workers/src/kill-switch.ts. The web side knows the same key
// shape but doesn't enforce it — the RLS policies + the table CHECK
// constraint do.
//
// Screen field audit (CLAUDE.md §19):
//   tile name         ← SERVICES[]
//   tile status pill  ← worstStatus() over daemons[]
//   tile last-sync    ← worstStatus().lastSeenIso
//   switch checked    ← useQuery(['settings','toggle',key]) → value === 'true'
//   switch optimistic ← React Query mutation w/ onMutate setQueryData

import { useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Calendar,
  DollarSign,
  type LucideIcon,
  Mail,
  MessageCircle,
  MessageSquare,
  Video,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  classifyHeartbeat,
  type HeartbeatRow,
  type HeartbeatStatus,
} from '@reverra/shared/heartbeat/read';
import GlassCard from '@/components/shared/GlassCard';
import PulseDot from '@/components/shared/PulseDot';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/lib/supabase';

// ============================================================================
// Toggle-key type (must match migration 0032's IN-allowlist exactly)
// ============================================================================
type ToggleKey =
  | 'bridge_capture_enabled'
  | 'bridge_imessage_enabled'
  | 'bridge_whatsapp_enabled'
  | 'embeddings_enabled'
  | 'email_triage_enabled'
  | 'commission_enabled'
  | 'calendar_enabled'
  | 'discord_bot_enabled';

// ============================================================================
// Service catalog
// ============================================================================
type ServiceDef = {
  name: string;
  description: string;
  icon: LucideIcon;
  /** Daemon names whose heartbeat indicates this service. If multiple,
   *  the worst status wins (alert > warning > alive > null). */
  daemons: string[];
  /** When set, render a <Switch> wired to this kill-switch key. */
  toggleKey?: ToggleKey;
  /** Optional clarifying line shown in the Switch's tooltip when its
   *  on/off semantics aren't obvious from the tile name alone. */
  toggleHint?: string;
  /** When set (and toggleKey is not), render a "Managed" badge with
   *  this tooltip text. For services with no owner-controllable
   *  on/off — webhook receivers, global API keys, etc. */
  managedNote?: string;
  /** Manual-only flag — disables status derivation, renders a static
   *  "Manual" tag instead. */
  manual?: boolean;
  /** Caption shown on manual services in place of the sync state. */
  manualCaption?: string;
};

const SERVICES: ServiceDef[] = [
  {
    name: 'Gmail',
    description: 'Inbox triage + drafts',
    icon: Mail,
    daemons: [
      'workers_gmail_parser',
      'workers_gmail_watch_renewal',
      'workers_email_drafts_writeback',
    ],
    toggleKey: 'email_triage_enabled',
  },
  {
    name: 'Google Calendar',
    description: 'Events + focus defense',
    icon: Calendar,
    daemons: ['workers_calendar_parser', 'workers_calendar_renewal'],
    toggleKey: 'calendar_enabled',
  },
  {
    name: 'Fathom',
    description: 'Meeting transcripts',
    icon: Video,
    daemons: ['workers_fathom_parser', 'workers_meeting_brief'],
    managedNote: 'Always-on edge function. Disable by pausing in the Fathom dashboard.',
  },
  {
    name: 'iMessage / SMS',
    description: 'Mac bridge — text capture',
    icon: MessageSquare,
    daemons: ['bridge_imessage'],
    toggleKey: 'bridge_imessage_enabled',
  },
  {
    name: 'WhatsApp',
    description: 'Mac bridge — linked device',
    icon: MessageSquare,
    daemons: ['bridge_whatsapp'],
    toggleKey: 'bridge_whatsapp_enabled',
  },
  {
    name: 'Discord',
    description: 'Approval notifications',
    icon: MessageCircle,
    daemons: ['workers_discord_bot', 'workers_discord_poster'],
    toggleKey: 'discord_bot_enabled',
    // Surfaced as a hint in the switch's parent tooltip — toggling off
    // pauses outbound posts but the bot's slash-command listener stays
    // alive so /status keeps responding. R1 reviewer N2.
    toggleHint: 'Pauses outbound posts; bot stays online for slash commands.',
  },
  {
    name: 'Gusto',
    description: 'Commission CSV upload',
    icon: DollarSign,
    daemons: [],
    manual: true,
    manualCaption: 'Manual CSV upload from /commissions',
  },
];

// ============================================================================
// Heartbeat aggregation (unchanged from W4)
// ============================================================================
// 'asleep' sits above alive but below warning: a capture daemon on a sleeping
// laptop is expected, not degraded.
const STATUS_ORDER: Record<HeartbeatStatus, number> = {
  alive: 0,
  asleep: 1,
  warning: 2,
  alert: 3,
};

function worstStatus(
  rows: HeartbeatRow[],
  daemonNames: string[],
  now: Date,
): { status: HeartbeatStatus | null; lastSeenIso: string | null } {
  let worst: HeartbeatStatus | null = null;
  let lastSeenIso: string | null = null;
  let anyPresent = false;
  for (const name of daemonNames) {
    const row = rows.find((r) => r.daemon_name === name);
    if (!row || !row.payload) continue;
    anyPresent = true;
    // Pass the daemon name so a capture reader on a sleeping Mac reports
    // 'asleep' rather than 'Stopped'.
    const status = classifyHeartbeat(row.payload, now, { daemonName: row.daemon_name }).status;
    if (!worst || STATUS_ORDER[status] > STATUS_ORDER[worst]) worst = status;
    if (!lastSeenIso || row.payload.last_seen_at > lastSeenIso) {
      lastSeenIso = row.payload.last_seen_at;
    }
  }
  return { status: anyPresent ? worst : null, lastSeenIso };
}

// ============================================================================
// Toggle data layer
// ============================================================================
async function fetchToggle(key: ToggleKey): Promise<boolean> {
  // Throws on RLS/network error so the consumer's `isError` is truthful.
  // The old behavior — "any error → return true" — masked an RLS
  // regression as "switch is on" and was R1 reviewer S1.
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle();
  if (error) throw error;
  // Migration 0032 seeds every toggle row, so `data === null` in
  // steady state means either (a) the auth-bootstrap window before
  // the owner role resolves, or (b) RLS hid the row from a non-owner
  // JWT (shouldn't happen — Settings is owner-gated). Warn in dev so
  // a real RLS regression is visible at the console, default true
  // for UX. R2 reviewer N-R2-1.
  if (!data) {
    if (import.meta.env.DEV) {
      console.warn(
        `fetchToggle: null data for key=${key} — RLS hid the seed row, or pre-bootstrap`,
      );
    }
    return true;
  }
  return data.value === 'true';
}

async function writeToggle(key: ToggleKey, next: boolean): Promise<void> {
  // .select() so we see the affected row count and can detect a silent
  // zero-row UPDATE — that would mean RLS rejected the policy match
  // (e.g. seed missing, key drift, future allowlist tightening). R1
  // reviewer S2: this turns a silent UI desync into a loud failure.
  const { data, error } = await supabase
    .from('app_settings')
    .update({ value: next ? 'true' : 'false' })
    .eq('key', key)
    .select('key');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(`Toggle write affected zero rows for key=${key} (RLS or seed mismatch)`);
  }
}

// ============================================================================
// Per-tile Switch (only mounted for tiles with toggleKey)
// ============================================================================
function ToggleSwitch({
  toggleKey,
  label,
  hint,
}: {
  toggleKey: ToggleKey;
  label: string;
  hint?: string;
}): JSX.Element {
  const qc = useQueryClient();
  const queryKey = ['settings', 'toggle', toggleKey] as const;

  const q = useQuery({
    queryKey,
    queryFn: () => fetchToggle(toggleKey),
    refetchInterval: 10_000,
    retry: 0,
  });

  // Local "pending" flag prevents the user from rapid-fire flipping
  // while the mutation is still in flight. Optimistic update means the
  // visual state is already updated; this gates input, not paint.
  const [pending, setPending] = useState(false);

  const mut = useMutation({
    mutationFn: (next: boolean) => writeToggle(toggleKey, next),
    onMutate: async (next) => {
      setPending(true);
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<boolean>(queryKey);
      qc.setQueryData<boolean>(queryKey, next);
      return { prev };
    },
    onError: (err, _next, ctx) => {
      // Roll back on RLS / network failure.
      if (ctx?.prev !== undefined) qc.setQueryData<boolean>(queryKey, ctx.prev);
      toast.error(`Couldn’t update ${label}`, {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    },
    onSuccess: (_void, next) => {
      const prev = !next;
      toast(next ? `${label} enabled` : `${label} paused`, {
        description: next
          ? 'Daemon will resume on its next tick (≤60s).'
          : 'Daemon will pause on its next tick (≤60s).',
        duration: 8000,
        action: {
          label: 'Undo',
          onClick: () => mut.mutate(prev),
        },
      });
    },
    onSettled: () => {
      setPending(false);
      qc.invalidateQueries({ queryKey });
    },
  });

  const checked = q.data ?? true;

  // R1 reviewer S1: render the switch DISABLED and tooltip the error
  // when the toggle read fails (RLS regression, network blip). Default-
  // -to-true behavior would hide a real failure as "switch is on".
  if (q.isError) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Switch
              checked={false}
              disabled
              aria-label={`${label} status unavailable`}
              onCheckedChange={() => {}}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[220px] text-[11px]">
          Couldn’t read this toggle. {q.error instanceof Error ? q.error.message : 'Unknown error.'}
        </TooltipContent>
      </Tooltip>
    );
  }

  const switchEl = (
    <Switch
      checked={checked}
      onCheckedChange={(next) => mut.mutate(next)}
      disabled={pending || q.isLoading}
      aria-label={`${label} enabled`}
    />
  );

  if (!hint) return switchEl;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{switchEl}</span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[220px] text-[11px]">
        {hint}
      </TooltipContent>
    </Tooltip>
  );
}

// ============================================================================
// Tile
// ============================================================================
type ServiceTileProps = {
  service: ServiceDef;
  rows: HeartbeatRow[];
  now: Date;
};

function ServiceTile({ service, rows, now }: ServiceTileProps): JSX.Element {
  const Icon = service.icon;

  if (service.manual) {
    return (
      <GlassCard hover={false}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg neo-chip bg-secondary flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-muted-foreground" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{service.name}</p>
            <p className="text-xs text-muted-foreground truncate">{service.description}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-muted-foreground/40" />
            Manual
          </span>
          <span className="italic truncate ml-2">{service.manualCaption}</span>
        </div>
      </GlassCard>
    );
  }

  const { status, lastSeenIso } = worstStatus(rows, service.daemons, now);

  const dotColor =
    status === 'alive'
      ? 'green'
      : status === 'asleep'
        ? 'green'
        : status === 'warning'
          ? 'amber'
          : status === 'alert'
            ? 'red'
            : 'amber';
  const statusLabel =
    status === 'alive'
      ? 'Connected'
      : status === 'asleep'
        ? 'Asleep — Mac shut'
        : status === 'warning'
          ? 'Stale'
          : status === 'alert'
            ? 'Stopped'
            : 'Not reporting';
  const statusTextClass =
    status === 'alive'
      ? 'text-forest'
      : status === 'asleep'
        ? 'text-muted-foreground'
        : status === 'warning'
          ? 'text-attention'
          : status === 'alert'
            ? 'text-alert'
            : 'text-muted-foreground';
  const iconBgClass = status === 'alive' ? 'bg-forest-soft' : 'bg-secondary';
  const iconColorClass = status === 'alive' ? 'text-forest' : 'text-muted-foreground';

  const lastSyncCaption = lastSeenIso
    ? `Synced ${formatDistanceToNowStrict(new Date(lastSeenIso), { addSuffix: true })}`
    : 'No heartbeat yet';

  return (
    <GlassCard hover={false}>
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg neo-chip ${iconBgClass} flex items-center justify-center shrink-0`}
        >
          <Icon className={`w-5 h-5 ${iconColorClass}`} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{service.name}</p>
          <p className="text-xs text-muted-foreground truncate">{service.description}</p>
        </div>
        {service.toggleKey ? (
          <ToggleSwitch
            toggleKey={service.toggleKey}
            label={service.name}
            hint={service.toggleHint}
          />
        ) : service.managedNote ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-[10px] uppercase tracking-wide text-muted-foreground border rounded px-1.5 py-0.5 cursor-help"
                aria-label="Managed service — not owner-toggleable"
              >
                Managed
              </span>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[220px] text-[11px]">
              {service.managedNote}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px]">
        <span className={`inline-flex items-center gap-1.5 ${statusTextClass}`}>
          <PulseDot color={dotColor} />
          {statusLabel}
        </span>
        <span className="text-muted-foreground truncate ml-2">{lastSyncCaption}</span>
      </div>
    </GlassCard>
  );
}

type ConnectedServicesCardProps = {
  rows: HeartbeatRow[];
  now: Date;
};

export default function ConnectedServicesCard({
  rows,
  now,
}: ConnectedServicesCardProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {SERVICES.map((service) => (
        <ServiceTile key={service.name} service={service} rows={rows} now={now} />
      ))}
    </div>
  );
}
